import { useTranslation } from 'react-i18next';
import { BackButton } from '../components/BackButton.js';
import { CallUsNotice } from '../components/CallUsNotice.js';
import type { VenueDetail } from '../lib/api.js';
import { formatDayChip, formatHijri } from '../lib/time.js';

/**
 * The date strip.
 *
 * Every chip carries the Hijri day beneath the Gregorian one. That is not
 * decoration: during Ramadan a Qatari guest thinks in Hijri, and a picker that
 * only speaks Gregorian makes them count days in their head before choosing.
 *
 * The strip scrolls horizontally and mirrors with the page, so the nearest date
 * sits at the inline start in both directions - "soonest" is always where the
 * reading begins.
 */
export function DateStep({
  dates,
  value,
  venue,
  locale,
  onChange,
  onNext,
  onBack,
}: {
  dates: string[];
  value: string;
  venue: VenueDetail;
  locale: string;
  onChange: (next: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const venueTime = {
    timezone: venue.timezone,
    serviceDayCutoffHour: 6,
    digitStyle: venue.digit_style,
  };

  return (
    <section className="flex flex-col gap-4">
      <BackButton onClick={onBack} />
      <h2 className="text-sm font-medium">{t('common.date')}</h2>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {dates.map((date, index) => {
          const chip = formatDayChip(date, venueTime, locale);
          const selected = date === value;

          return (
            <button
              key={date}
              type="button"
              data-testid="date-chip"
              onClick={() => {
                onChange(date);
                onNext();
              }}
              aria-pressed={selected}
              className={[
                'flex min-w-16 shrink-0 flex-col items-center gap-0.5 rounded-lg border px-2 py-2 transition-colors',
                selected
                  ? 'border-brand-teal bg-brand-teal text-white'
                  : 'border-slate-200 hover:border-brand-teal',
              ].join(' ')}
            >
              <span className="text-[11px] opacity-80">
                {index === 0 ? t('common.today') : chip.weekday}
              </span>
              <span className="text-lg font-semibold" data-numeric>
                {chip.day}
              </span>
              <span className="text-[10px] opacity-70">
                {formatHijri(date, venueTime, locale)}
              </span>
            </button>
          );
        })}
      </div>

      {/* dates[0] is today's service date, which is what the guest sees first.
          A same-day booking depends on what has already walked in tonight, so
          it needs whoever is on the phone rather than the availability engine. */}
      {value === dates[0] ? <CallUsNotice reason="sameDay" /> : null}

      <button
        type="button"
        onClick={onNext}
        className="rounded-lg bg-brand-teal py-3 font-medium text-white hover:bg-brand-teal-dark"
      >
        {t('widget.next')}
      </button>
    </section>
  );
}
